const BASE_URL = process.env.API_URL ?? '/api';

export async function apiRequest<T>(token: string, path: string, body: unknown = {}): Promise<T> {
    const csrf = document.cookie
        .split('; ')
        .find((item) => item.startsWith('vstack-memhub-csrf='))
        ?.split('=')[1];
    const response = await fetch(`${BASE_URL}/${path.replace(/^\//, '')}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
            'Content-Type': 'application/json',
            'X-API-Version': 'latest',
        },
        body: JSON.stringify(body),
    });
    if (response.status === 401) window.dispatchEvent(new Event('vstack-auth-expired'));
    if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `${response.status} ${response.statusText}`);
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
}

export async function apiStream(
    token: string,
    path: string,
    handlers: { signal: AbortSignal; onChunk: (text: string) => void },
): Promise<void> {
    const response = await fetch(path, {
        credentials: 'same-origin',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'X-API-Version': 'latest' },
        signal: handlers.signal,
    });
    if (response.status === 401) window.dispatchEvent(new Event('vstack-auth-expired'));
    if (!response.ok) throw new Error((await response.text()) || `${response.status} ${response.statusText}`);
    if (!response.body) throw new Error('Streaming is not supported by this browser');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            const text = decoder.decode(value, { stream: true });
            if (text) handlers.onChunk(text);
        }
        const tail = decoder.decode();
        if (tail) handlers.onChunk(tail);
    } catch (reason) {
        try {
            await reader.cancel();
        } catch {
            // The stream is already closed or errored.
        }
        throw reason;
    } finally {
        reader.releaseLock();
    }
}
